import React, { useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { get as _get } from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HealthIcon from '@mui/icons-material/Favorite';
import SettingsIcon from '@mui/icons-material/Settings';
import FrownyIcon from '@mui/icons-material/SentimentVeryDissatisfied';

import Auth from '../../data/auth-service';
import { blue } from '../../data/colors';

import { IsEmpty, FriendlyPolicyName } from '../../libraries/helpers';

const useRowStyles = makeStyles(theme => ({
    root: {
        '& > *': {
            borderBottom: 'unset',
        }
    },
    status: {
        height: '20px',
        width: '20px',
        borderRadius: '20px',
    },
    normalText: {
        color: theme.palette.success.main,
    },
    warningText: {
        color: theme.palette.warning.main,
    },
    criticalText: {
        color: theme.palette.error.main,
    },
    normal: {
        background: theme.palette.success.main,
    },
    warning: {
        background: theme.palette.warning.main,
    },
    critical: {
        background: theme.palette.error.main,
    },
    sensorCollapse: {
        background: theme.palette.mode === 'light' ? '#F9F9F9' : blue[1000],
        '& .MuiTableCell-root.MuiTableCell-body': {
            padding: '2px 16px',
        }
    },
    policyName: {
        display: 'inline-block',
        background: theme.palette.primary.main,
        margin: '3px',
        borderRadius: '3px',
        padding: '2px 8px',
    }
}));

const CMCRow = (props) => {
    const classes = useRowStyles();
    const theme = useTheme();
    const { row } = props;
    const [open, setOpen] = useState(false);
    const sensorPerms = Auth.permissions.get('Sensors');

    useEffect(() => {
        setOpen(props.expandAll);
    }, [props.expandAll]);

    const cmcJobs = useMemo(() => {
        return props.allJobs?.filter(j => j.cmcUuid === row.uuid) || [];
    }, [props.allJobs, row.uuid]);

    const policyMap = useMemo(() => {
        const map = new Map();
        props.policies?.forEach((policyType, typeIdx) => {
            policyType.policies?.forEach((policy, policyIdx) => {
                const key = `${policyType.type}|${policy.name}`;
                map.set(key, {
                    typeIdx,
                    policyIdx,
                    color: policy.color || theme.palette.primary.main
                });
            });
        });
        return map;
    }, [props.policies, theme.palette.primary.main]);


    const handleOpen = () => {
        setOpen(!open);
    };

    const handleCMCHealth = () => {
        props.handleSelectCMC(row.uuid);
    };

    const handleCMCSettings = () => {
        props.handleCMCSettings(row.uuid);
    };

    const handleSensorHealth = (sensor) => {
        props.handleSelectSensor(sensor.cmc, sensor.uuid);
    };

    return (
        <React.Fragment>
            <TableRow className={classes.root}>
                <TableCell>
                    <IconButton aria-label="expand sensor row" size="small" onClick={handleOpen}>
                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <IconButton size="small" aria-label="health" onClick={handleCMCHealth}><HealthIcon /></IconButton>
                    <IconButton size="small" aria-label="settings" onClick={handleCMCSettings}><SettingsIcon /></IconButton>
                </TableCell>
                <TableCell>
                    <Tooltip title={row.status}>
                        <Box className={clsx({
                            [classes.status]: true,
                            [classes[row.status]]: true,
                        })}></Box>
                    </Tooltip>
                </TableCell>
                <TableCell><Typography variant="h6">{row.name}</Typography></TableCell>
                <TableCell align="right">{_get(row, 'host', '')}</TableCell>
                <TableCell align="right">{_get(row, 'model', '')}</TableCell>
                <TableCell align="right">{_get(row, 'health_metrics.version', '')}</TableCell>
                <TableCell align="right">
                    {row.standby}
                </TableCell>
            </TableRow>
            <TableRow className={classes.sensorCollapse}>
                <TableCell style={{ padding: 0 }} colSpan={7}>
                    <Collapse in={open}>
                        <Box m={2} display={props.showIssues ? 'block' : 'none'}>
                            <Grid container>
                                <Grid item xs={6}>
                                    <Box display={(() => {
                                        if ((_get(row, 'health_metrics.issues', []) || []).length || _get(row, 'health_status.status', '') !== 'normal') {
                                            return 'block';
                                        }
                                        return 'none';
                                    })()}><Typography variant="h6" gutterBottom>Issues</Typography></Box>
                                    {_get(row, 'health_metrics.issues', []).map((v, i) => {
                                        if (v.message !== _get(row, 'health_status.msg', '')) {
                                            return (
                                                <Box
                                                    key={'cmc-issue-' + row.name + '-' + i}
                                                    className={classes[row.status + 'Text']}
                                                >
                                                    {v.message}
                                                </Box>
                                            );
                                        }
                                    })}
                                    <Box
                                        className={classes[row.health_status.status + 'Text']}
                                        display={_get(row, 'health_status.msg', '').length ? 'block' : ' none'}
                                    >
                                        {row.health_status.msg}
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                     <Box display={cmcJobs.length > 0 ? 'block' : 'none'}>
                                        <Typography variant="h6" gutterBottom>Policies</Typography>
                                    </Box>
                                    {cmcJobs.map((job, i) => {
                                        // O(1) lookup using memoized policy map
                                        const key = `${job.jobType}|${job.name}`;
                                        const policyData = policyMap.get(key);
                                        const color = policyData?.color || theme.palette.primary.main;
                                         return (
                                         <Tooltip key={`policy-${job.jobType}-${i}`} title={FriendlyPolicyName(job.jobType)}>
                                                <Box
                                                    className={classes.policyName}
                                                    style={{
                                                        background: color,
                                                        color: theme.palette.getContrastText(color)
                                                    }}
                                                >{job.name}</Box>
                                            </Tooltip>
                                         );
                                    })}
                                </Grid>
                            </Grid>
                        </Box>
                        <Box mt={1}>
                            <Box px={2} display={!row.sensors || !row.sensors.length ? 'block' : 'none'}>
                                <Box width="145px" m={'auto'}>
                                    {(() => {
                                        switch (row.standby) {
                                            case 'Standby': return <span>Standby CMC</span>;
                                            default: return <span>No sensors found <FrownyIcon /></span>;
                                        }
                                    })()}
                                </Box>
                            </Box>
                            {!row.sensors && row.loading &&
                                <Box px={2} display='block'>
                                    <Box m={'auto'}>
                                        <CircularProgress size={20} style={{ marginRight: 10 }}/>
                                        Fetching sensors...
                                    </Box>
                                </Box>
                            }
                            <Box display={row.sensors ? row.sensors.length ? 'block' : 'none' : 'none'}>
                                <TableContainer component={Paper} className={classes.sensorCollapse}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><Typography variant="h6">Sensors</Typography></TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Name</TableCell>
                                                <TableCell align="right">Host</TableCell>
                                                <TableCell align="right">Model</TableCell>
                                                <TableCell align="right">Version</TableCell>
                                                <TableCell align="right">Suricata Ver.</TableCell>
                                                <TableCell align="right">Zeek Ver.</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(row.sensors ? row.sensors : []).map((v, i) => {
                                                return (
                                                    <TableRow key={v.uuid}>
                                                        <TableCell>
                                                            { sensorPerms.read && <IconButton size="small" aria-label="sensor health" onClick={() => { handleSensorHealth(v) }}>
                                                                <HealthIcon />
                                                            </IconButton>}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Tooltip title={v.status}>
                                                                <Box className={clsx({
                                                                    [classes.status]: true,
                                                                    [classes[v.status]]: true,
                                                                })}></Box>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell>{v.data.name}</TableCell>
                                                        <TableCell align="right">{v.host}</TableCell>
                                                        <TableCell align="right">{v.model}</TableCell>
                                                        <TableCell align="right">{_get(v, 'health_metrics.version', 'N/A')}</TableCell>
                                                        <TableCell align="right">{_get(v, 'health_metrics.suricata_version', 'N/A')}</TableCell>
                                                        <TableCell align="right">{_get(v, 'health_metrics.zeek_version', 'N/A')}</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};

CMCRow.propTypes = {
    row: PropTypes.object.isRequired,
    policies: PropTypes.array.isRequired,
    allJobs: PropTypes.array.isRequired,
    showIssues: PropTypes.bool,
    expandAll: PropTypes.bool,
    handleSelectCMC: PropTypes.func.isRequired,
    handleCMCSettings: PropTypes.func.isRequired,
    handleSelectSensor: PropTypes.func.isRequired,
};

export default CMCRow;
